import React, {useState} from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    List,
    ListItem,
    Stack,
    Switch,
    Tooltip,
} from "@mui/material";
import Typography from "@mui/material/Typography";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import VerticalAlignBottomIcon from "@mui/icons-material/VerticalAlignBottom";
import VerticalAlignTopIcon from "@mui/icons-material/VerticalAlignTop";
import type {Editor} from "@tiptap/core";
import type {PrintoutType} from "crqlar-react-common/dist/models/printout/printout.model";
import {useTranslation} from "react-i18next";
import {getPrintoutInsertBlocks, type PrintoutBlockDescriptor} from "../../blocks";
import {
    findPrintoutRegion,
    isPrintoutRegionEmpty,
    type PrintoutRegionKind,
} from "../../blocks/headerFooter/printoutRegionOptions";
import {
    insertPrintoutBlockAtSelection,
    insertPrintoutRegion,
} from "../printoutBlockInsert";
import {endPrintoutDrag, startPrintoutPaletteBlockDrag} from "../dnd/PrintoutDragAndDrop";
import {PanelSection} from "./PrintoutPanelSection";
import {usePrintoutDocumentRegions} from "./useSelectedPrintoutBlock";

type Props = {
    editor: Editor | null
    printoutType?: PrintoutType | null
    disabled?: boolean
};

const LEADING_ICON_SLOT_WIDTH = 30;

// Literal keys are required by the translation collector.
const REGION_LABELS: Record<PrintoutRegionKind, string> = {
    footer: "Page footer",
    header: "Page header",
};


const REGION_REMOVE_TITLES: Record<PrintoutRegionKind, string> = {
    footer: "Remove the footer?",
    header: "Remove the header?",
};

const REGION_REMOVE_BODIES: Record<PrintoutRegionKind, string> = {
    footer: "The footer has content. Turning it off deletes that content from the document.",
    header: "The header has content. Turning it off deletes that content from the document.",
};

const REGION_KINDS: PrintoutRegionKind[] = ['header', 'footer'];

function PaletteRow({disabled, dragging, icon, label, onActivate, onDragEnd, onDragStart, tooltip}: {
    disabled: boolean
    dragging: boolean
    icon: React.ReactNode
    label: string
    onActivate: () => void
    onDragEnd: () => void
    onDragStart: (event: React.DragEvent<HTMLElement>) => void
    tooltip?: string
}) {
    const activateRow = (event: React.KeyboardEvent<HTMLElement>) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;

        event.preventDefault();
        onActivate();
    };

    return (
        <Tooltip placement={'right'} title={tooltip ?? ''}>
            <ListItem
                aria-disabled={disabled}
                disableGutters
                draggable={!disabled}
                onClick={() => !disabled && onActivate()}
                onDragEnd={onDragEnd}
                onDragStart={onDragStart}
                onKeyDown={(event) => !disabled && activateRow(event)}
                role={'button'}
                tabIndex={disabled ? -1 : 0}
                sx={{
                    borderRadius: 1,
                    color: disabled ? 'text.disabled' : 'text.primary',
                    cursor: disabled ? 'default' : 'grab',
                    gap: 1,
                    mb: 0.5,
                    minHeight: 44,
                    opacity: dragging ? 0.5 : 1,
                    px: 1,
                    '&:hover': {
                        bgcolor: disabled ? 'transparent' : 'action.hover',
                    },
                    '&:active': {
                        cursor: disabled ? 'default' : 'grabbing',
                    },
                }}
            >
                <Box
                    sx={{
                        color: 'text.disabled',
                        display: 'flex',
                        flexShrink: 0,
                        justifyContent: 'center',
                        width: LEADING_ICON_SLOT_WIDTH,
                    }}
                >
                    <DragIndicatorIcon fontSize={'small'}/>
                </Box>

                <Box sx={{color: disabled ? 'text.disabled' : 'text.secondary', display: 'flex', flexShrink: 0}}>
                    {icon}
                </Box>

                <Typography
                    noWrap
                    sx={{flex: 1, fontSize: 14, fontWeight: 500, minWidth: 0}}
                >
                    {label}
                </Typography>
            </ListItem>
        </Tooltip>
    );
}

function RegionToggleRow({checked, disabled, kind, onChange}: {
    checked: boolean
    disabled: boolean
    kind: PrintoutRegionKind
    onChange: (checked: boolean) => void
}) {
    const {t} = useTranslation();

    return (
        <Box
            sx={{
                alignItems: 'center',
                display: 'flex',
                gap: 1.25,
                minHeight: 56,
                px: 1,
                py: 0.5,
            }}
        >
            <Box
                sx={{
                    alignItems: 'center',
                    color: disabled ? 'text.disabled' : 'text.secondary',
                    display: 'flex',
                    flexShrink: 0,
                    justifyContent: 'center',
                    width: LEADING_ICON_SLOT_WIDTH,
                }}
            >
                {kind === 'header'
                    ? <VerticalAlignTopIcon fontSize={'small'}/>
                    : <VerticalAlignBottomIcon fontSize={'small'}/>}
            </Box>

            <Box sx={{flex: 1, minWidth: 0}}>
                <Typography
                    color={disabled ? 'text.disabled' : 'text.primary'}
                    sx={{fontSize: 14, fontWeight: 500}}
                >
                    {t(REGION_LABELS[kind])}
                </Typography>
            </Box>

            <Switch
                checked={checked}
                disabled={disabled}
                inputProps={{'aria-label': t(REGION_LABELS[kind])}}
                onChange={(event) => onChange(event.target.checked)}
                size={'small'}
            />
        </Box>
    );
}

export function PrintoutInsertPanel({editor, printoutType, disabled}: Props) {
    const {t} = useTranslation();
    const [draggingRowId, setDraggingRowId] = useState<string | null>(null);
    const [pendingRegionRemoval, setPendingRegionRemoval] = useState<PrintoutRegionKind | null>(null);
    const {hasFooter, hasHeader} = usePrintoutDocumentRegions(editor);

    const insertDisabled = !editor || !!disabled;
    const blocks = getPrintoutInsertBlocks(printoutType);
    const regionExists: Record<PrintoutRegionKind, boolean> = {footer: hasFooter, header: hasHeader};

    const insertBlock = (block: PrintoutBlockDescriptor) => {
        if (!editor || disabled) return;

        insertPrintoutBlockAtSelection(editor, block);
    };

    const endDrag = () => {
        endPrintoutDrag(editor);
        setDraggingRowId(null);
    };

    const startBlockDrag = (event: React.DragEvent<HTMLElement>, block: PrintoutBlockDescriptor) => {
        if (!editor || insertDisabled) {
            event.preventDefault();
            return;
        }

        startPrintoutPaletteBlockDrag(editor, event.dataTransfer, block.id);
        setDraggingRowId(block.id);
    };

    const removeRegion = (kind: PrintoutRegionKind) => {
        if (!editor || disabled) return;

        const chain = editor.chain().focus();

        if (kind === 'header') chain.unsetPrintoutHeader().run();
        else chain.unsetPrintoutFooter().run();

        setPendingRegionRemoval(null);
    };

    const requestRegionRemoval = (kind: PrintoutRegionKind) => {
        if (!editor || disabled) return;

        const region = findPrintoutRegion(editor.state.doc, kind);

        if (!region || isPrintoutRegionEmpty(region.node)) {
            removeRegion(kind);
            return;
        }

        setPendingRegionRemoval(kind);
    };

    const setRegionEnabled = (kind: PrintoutRegionKind, enabled: boolean) => {
        if (!editor || disabled) return;

        if (enabled) {
            insertPrintoutRegion(editor, kind);
            return;
        }

        requestRegionRemoval(kind);
    };

    return (
        <Stack sx={{height: '100%', minHeight: 0}}>
            <Box sx={{flex: 1, minHeight: 0, overflowY: 'auto', px: 2, pt: 0.5, pb: 1.5}}>
                <Stack gap={3} sx={{pt: 3}}>
                    <PanelSection title={t('Blocks')}>
                        <List disablePadding>
                            {blocks.map((block) => (
                                <PaletteRow
                                    disabled={insertDisabled}
                                    dragging={draggingRowId === block.id}
                                    icon={block.icon}
                                    key={block.id}
                                    label={t(block.label)}
                                    onActivate={() => insertBlock(block)}
                                    onDragEnd={endDrag}
                                    onDragStart={(event) => startBlockDrag(event, block)}
                                />
                            ))}
                        </List>
                    </PanelSection>

                    <PanelSection title={t('Layout')}>
                        <Stack>
                            {REGION_KINDS.map((kind) => (
                                <RegionToggleRow
                                    checked={regionExists[kind]}
                                    disabled={insertDisabled}
                                    key={kind}
                                    kind={kind}
                                    onChange={(checked) => setRegionEnabled(kind, checked)}
                                />
                            ))}
                        </Stack>
                    </PanelSection>
                </Stack>
            </Box>

            <Dialog
                fullWidth
                maxWidth={'xs'}
                onClose={() => setPendingRegionRemoval(null)}
                open={pendingRegionRemoval !== null}
            >
                <DialogTitle>
                    {pendingRegionRemoval ? t(REGION_REMOVE_TITLES[pendingRegionRemoval]) : ''}
                </DialogTitle>
                <DialogContent>
                    <Typography color={'text.secondary'} fontSize={14}>
                        {pendingRegionRemoval ? t(REGION_REMOVE_BODIES[pendingRegionRemoval]) : ''}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPendingRegionRemoval(null)}>{t('Cancel')}</Button>
                    <Button
                        color={'error'}
                        disabled={disabled}
                        onClick={() => pendingRegionRemoval && removeRegion(pendingRegionRemoval)}
                        variant={'contained'}
                    >
                        {t('Remove')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}

export default PrintoutInsertPanel;
